import React from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaMobileAlt, FaBrain, FaChartLine, FaArrowRight } from 'react-icons/fa';

// Import local assets
import MobileImg from '../assets/Mobile_App_Development_Company_Israel___Skilled_App_Developers-removebg-preview.png';
import DigitalMarketingImg from '../assets/Grow your brand across Multiple digital platforms instead of one __#socialmedia #marketing #socialmediamarketing #digitalmarketing #instagram #branding #business #marketingdigital #seo #design.png';
import AIImg from '../assets/AIBgRemoved.png';
import WebDevImg from '../assets/WebDev.png';

const services = [
    {
        id: 1,
        title: "Mastering Web Development",
        subtitle: "Scalable Platforms",
        description: "We build responsive, high-performance websites and web applications tailored to your business needs, ensuring seamless user experiences and robust functionality.",
        icon: FaCode,
        image: WebDevImg,
        theme: "bg-gradient-to-br from-[#0f172a] via-[#155e75] to-[#164e63]", // Deep Cyan
        accent: "text-cyan-300",
        pill: "bg-white/10 backdrop-blur-md",
        border: "border-white/20",
        btnColor: "bg-cyan-500 hover:bg-cyan-400 text-white"
    },
    {
        id: 2,
        title: "Next-Gen Mobile Apps",
        subtitle: "iOS & Android Natives",
        description: "Native and cross-platform mobile applications that engage users and drive growth for your brand. We leverage advanced frameworks to deliver fluid animations and intuitive interfaces.",
        icon: FaMobileAlt,
        image: MobileImg,
        theme: "bg-gradient-to-br from-[#2e1065] via-[#581c87] to-[#3b0764]", // Deep Purple
        accent: "text-purple-300",
        pill: "bg-white/10 backdrop-blur-md",
        border: "border-white/20",
        btnColor: "bg-purple-500 hover:bg-purple-400 text-white"
    },
    {
        id: 3,
        title: "Digital Growth & Brand Strategy",
        subtitle: "Reach Your Audience",
        description: "Comprehensive digital strategies aimed at maximizing your reach. From Social Media Management and SEO to targeted Ad Campaigns, we ensure your brand gets noticed.",
        icon: FaChartLine,
        image: DigitalMarketingImg,
        theme: "bg-gradient-to-br from-[#831843] via-[#be123c] to-[#881337]", // Rose/Red
        accent: "text-rose-300",
        pill: "bg-white/10 backdrop-blur-md",
        border: "border-white/20",
        btnColor: "bg-rose-500 hover:bg-rose-400 text-white"
    },
    {
        id: 4,
        title: "Future Ready AI & Automation",
        subtitle: "Smart Technology",
        description: "Leverage the power of Artificial Intelligence and Machine Learning to optimize your workflows and decision-making. Our AI models are designed to give you a competitive edge.",
        icon: FaBrain,
        image: AIImg,
        theme: "bg-gradient-to-br from-[#020617] via-[#4338ca] to-[#7c3aed]", // Indigo/Violet
        accent: "text-indigo-300",
        pill: "bg-white/10 backdrop-blur-md",
        border: "border-white/20",
        btnColor: "bg-indigo-500 hover:bg-indigo-400 text-white"
    }
];

const Services = () => {
    return (
        <section id="services" className="bg-slate-900 relative">
            {/* Scroll Container */}
            <div className="relative pb-0">
                {services.map((service, index) => (
                    <StickyCard key={service.id} data={service} index={index} />
                ))}
            </div>
        </section>
    );
};

const StickyCard = ({ data, index }) => {
    // Alternating Layout Logic
    const isEven = index % 2 === 0;

    return (
        <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden shadow-2xl perspectiv-1000">

            {/* Background Theme */}
            <div className={`absolute inset-0 ${data.theme}`}>
                {/* Texture Overlay - Tech Grid */}
                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] mix-blend-overlay pointer-events-none"></div>
                {/* Radial Gradient for Depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>

            <div className="max-w-7xl w-full mx-auto px-4 sm:px-12 grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-20 items-center relative z-10 h-full content-center">

                {/* Text Content - Minimalist Tech */}
                <div className={`relative ${isEven ? 'lg:order-1' : 'lg:order-2'} flex flex-col justify-center order-1`}>

                    {/* Floating Tech Elements */}
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        className="absolute -top-24 -left-24 w-64 h-64 border border-white/5 rounded-full border-dashed z-0 opacity-20 pointer-events-none"
                    ></motion.div>

                    {/* Watermark Number - Floating Behind */}
                    <div
                        className="absolute -top-10 -left-4 text-[120px] lg:text-[240px] font-black text-white/5 select-none leading-none z-0 pointer-events-none"
                        style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                    >
                        0{data.id}
                    </div>

                    <div className="relative z-10 pl-2 lg:pl-8">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className={`inline-flex items-center space-x-2 mb-2 px-3 py-1 rounded-full border ${data.border} shadow-sm w-fit ${data.pill}`}
                        >
                            <data.icon className={`text-xs ${data.accent}`} />
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/90" style={{ fontFamily: '"Outfit", sans-serif' }}>{data.subtitle}</span>
                        </motion.div>

                        <motion.h3
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-3xl md:text-6xl font-extrabold text-white mb-2 lg:mb-6 leading-tight lg:leading-[1.1] tracking-tight drop-shadow-lg"
                            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                        >
                            {data.title}
                        </motion.h3>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="text-sm lg:text-lg text-gray-200 leading-snug lg:leading-relaxed mb-2 lg:mb-8 font-light text-justify max-w-md lg:max-w-none"
                            style={{ fontFamily: '"Outfit", sans-serif' }}
                        >
                            {data.description}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <button className={`group flex items-center gap-2 px-5 py-2 lg:px-6 lg:py-3 rounded-xl border border-white/10 transition-all font-bold text-[10px] lg:text-xs uppercase tracking-widest shadow-lg hover:shadow-cyan-500/20 ${data.btnColor} hover:-translate-y-1`}>
                                <span style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Explore Capability</span>
                                <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                            </button>
                        </motion.div>
                    </div>
                </div>

                {/* Image Display */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
                    className={`relative ${isEven ? 'lg:order-2' : 'lg:order-1'} ${data.id === 2 ? 'h-[35vh]' : 'h-[50vh]'} lg:h-[500px] w-full flex items-center justify-center order-2 text-center mt-2 lg:mt-0`}
                >
                    {/* Glowing Backdrop for Image */}
                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-white/5 blur-3xl rounded-full opacity-30 animate-pulse`}></div>

                    <img
                        src={data.image}
                        alt={data.title}
                        className="max-w-full max-h-full object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500 relative z-10 scale-110 lg:scale-100 mx-auto"
                    />
                </motion.div>

            </div>
        </div>
    );
};

export default Services;
